import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import { filters2qs } from '@data-fair/components-app/filters/filters'

Vue.use(Vuex)

export default () => {
  return new Vuex.Store({
    state: {
      application: window.APPLICATION,
      datasets: [],
      selected: null,
      layers: []
    },
    getters: {
      tileserverUrl (state) {
        return state.application.apiUrl + '/remote-services/tileserver-koumoul/proxy'
      },
      config (state) {
        return state.application && state.application.configuration
      },
      datasetInfos (state, getters) {
        return getters.config && getters.config.datasets && Object.assign({}, ...getters.config.datasets.map(d => ({ [d.id]: d })))
      },
      detailsIdx (state, getters) {
        for (const layer of getters.config.layers) {
          if (layer.details.length || (layer.embed && layer.embed.type !== 'none') || layer.linkTemplate) return getters.config.hideSearchBar ? 0 : 1
        }
        return -1
      },
      legendIdx (state, getters) {
        if (getters.config.layers.length > 1) return Math.max(getters.config.hideSearchBar ? -1 : 0, getters.detailsIdx) + 1
        else if (!getters.config.layers.length) return -1
        else {
          const layer = getters.config.layers[0]
          if (layer.colors.type === 'single' && (['icon-single', 'line', 'shape'].includes(layer.shapes.type))) return -1
          else return Math.max(getters.config.hideSearchBar ? -1 : 0, getters.detailsIdx) + 1
        }
      },
      accessKey (state) {
        const last = state.application.exposedUrl.split('/').pop()
        const toks = last.split('%3A')
        if (toks.length === 2) return toks[0]
        return null
      }
    },
    mutations: {
      setAny (state, params) {
        Object.assign(state, params)
      },
      toggle (state, i) {
        if (this.getters.config.oneLayerVisible) {
          state.layers.forEach(l => { l.visible = false })
          state.layers[i].visible = true
        } else state.layers[i].visible = !state.layers[i].visible
      },
      filter (state, params) {
        Vue.set(state.layers, params.i, { ...state.layers[params.i], filters: params.filters })
      },
      select (state, selected) {
        if (selected) {
          const idx = Number(selected[0].source.split('-').pop())
          const concepts = (state.application.configuration.layers[idx].concepts || [])
          for (const layer in state.layers) {
            if (Number(layer) !== idx) {
              const conceptsFilter = []
              for (const field of concepts) {
                if (state.datasets[layer].schema.find(f => f['x-concept'] && f['x-concept'].id === field['x-concept'].id)) {
                  const value = field.separator ? JSON.parse(selected[0].properties[field.key]).join(',') : selected[0].properties[field.key]
                  conceptsFilter.push(`_c_${field['x-concept'].id}_in=${value}`)
                }
              }
              if (conceptsFilter.length) Vue.set(state.layers, layer, { ...state.layers[layer], conceptsFilter })
            }
          }
        } else {
          for (const layer in state.layers) Vue.set(state.layers, layer, { ...state.layers[layer], conceptsFilter: null })
        }
        state.selected = selected
      }
    },
    actions: {
      async init ({ commit, dispatch, getters }) {
        const datasets = []
        for (const layer of getters.config.layers) {
          try {
            const dataset = (await axios.get(getters.datasetInfos[layer.dataset.id].href)).data
            if (layer.shapes.type === 'circle' && layer.shapes.radius.type === 'variable') {
              const params = { size: 1, select: layer.shapes.radius.field, sort: layer.shapes.radius.field, finalizedAt: getters.datasetInfos[layer.dataset.id].finalizedAt }
              if (layer.filters && layer.filters.length) params.qs = filters2qs(layer.filters)
              const minValueCircle = (await axios.get(getters.datasetInfos[layer.dataset.id].href + '/lines', { params })).data.results.pop()[layer.shapes.radius.field]
              params.sort = '-' + params.sort
              const maxValueCircle = (await axios.get(getters.datasetInfos[layer.dataset.id].href + '/lines', { params })).data.results.pop()[layer.shapes.radius.field]
              Object.assign(dataset, { minValueCircle, maxValueCircle })
            }
            if (layer.colors.rangeOptions && layer.colors.rangeOptions.scheme.computed) {
              const params = { size: 1, select: layer.colors.rangeOptions.field, sort: layer.colors.rangeOptions.field, finalizedAt: getters.datasetInfos[layer.dataset.id].finalizedAt }
              if (layer.filters && layer.filters.length) params.qs = filters2qs(layer.filters)
              const minValueColor = (await axios.get(getters.datasetInfos[layer.dataset.id].href + '/lines', { params })).data.results.pop()[layer.colors.rangeOptions.field]
              params.sort = '-' + params.sort
              const maxValueColor = (await axios.get(getters.datasetInfos[layer.dataset.id].href + '/lines', { params })).data.results.pop()[layer.colors.rangeOptions.field]
              // if (layer.colors && layer.colors.rangeOptions && layer.colors.rangeOptions.ranges && layer.colors.rangeOptions.ranges.type === 'quantiles') {
              const percents = [0, ...Array.from({ length: layer.colors.rangeOptions.ranges.size - 1 }, (_, i) => (i + 1) * (100 / layer.colors.rangeOptions.ranges.size)), 100].join(',')
              const metrics = (await axios.get(getters.datasetInfos[layer.dataset.id].href + '/metric_agg', { params: { field: layer.colors.rangeOptions.field, metric: 'percentiles', percents, finalizedAt: getters.datasetInfos[layer.dataset.id].finalizedAt } })).data.metric
              // }
              Object.assign(dataset, { minValueColor, maxValueColor, quantiles: metrics.map((m, i) => ({ min: i ? metrics[i - 1].value : undefined, max: m.value })).slice(1) })
            } else if (layer.colors.type === 'field' && layer.labelField) {
              const params = { size: 1000, select: layer.colors.field + ',' + layer.labelField, collapse: layer.labelField, finalizedAt: getters.datasetInfos[layer.dataset.id].finalizedAt }
              if (layer.colors.labelSort) params.sort = layer.labelField
              if (layer.filters && layer.filters.length) params.qs = filters2qs(layer.filters)
              const colors = (await axios.get(getters.datasetInfos[layer.dataset.id].href + '/lines', { params })).data.results
              Object.assign(dataset, { colors })
            }
            datasets.push(dataset)
          } catch (err) {
            dispatch('setError', err)
          }
        }
        commit('setAny', { datasets })
      },
      async setError ({ state }, error) {
        console.error('report error', error)
        try {
          axios.post(state.application.href + '/error', { message: error.message || error })
        } catch (err) {
          console.log('Failed to report error', err)
        }
      }
    }
  })
}
